/**
 * Validate that a URL is safe for navigation (http/https only).
 * Prevents javascript: and other dangerous protocol URLs.
 * @param {string} url - URL to validate
 * @returns {boolean} true if the URL uses http or https protocol
 */
export default function isSafeUrl(url) {
    if (!url || typeof url !== 'string') return false;
    try {
        const parsed = new URL(url, window.location.origin);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
}

/**
 * Matches absolute https URLs only. Deliberately stricter than isSafeUrl —
 * see extractSafeEmbedSrc for why relative values are unsafe in an iframe.
 * Stricter than DataProductDetail's /^https?:\/\//i — do not loosen to match.
 */
const ABSOLUTE_HTTPS_URL = /^https:\/\//i;

/**
 * @typedef {Object} EmbedSrcResult
 * @property {string|null} src - Validated absolute https URL, or null
 * @property {string|null} reason - Why extraction failed; null on success.
 *   'empty' means no embed_code was supplied, which is expected and not a fault.
 */

/**
 * Extract the iframe src from a catalog `embed_code` string, returning it only
 * if it is a safe, absolute https URL.
 *
 * width/height/scrolling/frameborder are intentionally discarded: the
 * consuming component sizes itself from its container, and publisher-supplied
 * heights (500px, 800px) vary too much to lay out consistently.
 *
 * @param {string} embedCode - Raw HTML from the catalog
 * @returns {EmbedSrcResult}
 */
export function extractSafeEmbedSrc(embedCode) {
    if (!embedCode || typeof embedCode !== 'string') {
        return { src: null, reason: 'empty' };
    }

    let doc;
    try {
        doc = new DOMParser().parseFromString(embedCode, 'text/html');
    } catch {
        return { src: null, reason: 'parse-failed' };
    }

    const iframe = doc.querySelector('iframe');
    if (!iframe) return { src: null, reason: 'no-iframe' };

    const src = iframe.getAttribute('src')?.trim();
    if (!src) return { src: null, reason: 'no-src' };

    if (!ABSOLUTE_HTTPS_URL.test(src)) {
        return { src: null, reason: 'not-absolute-https' };
    }

    if (!isSafeUrl(src)) return { src: null, reason: 'unsafe-url' };

    return { src, reason: null };
}
